import * as R from 'ramda';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { createSelector } from 'reselect';

import { getUseCustomerRegistry } from 'reducers/customerSearch';

dayjs.extend(relativeTime);

/** matches(regex)(element) - Returns true if the given element matches the regex */
export const matches = regex => c => {
  const key = c?.key?.replace(/\W/gi, '').replace('0', '');
  return regex.test(key);
};

export const notMatches = regex => R.complement(matches(regex));

/** Returns a disabled version of the passed-in field */
export const disableField = R.assocPath(['props', 'field', 'disabled'], true);

/** Changes the title of the matching field with what was provided */
export const renameField = value => R.assocPath(['props', 'title'], value);

export const insertBefore = R.curry((regex, item, list) => {
  const index = R.findIndex(matches(regex), list);
  return R.insert(index, item, list);
});

/**
 * append(regex, element)(children)
 * After the first match of regex, insert the given element into the children list
 */
export const insertAfter = R.curry((regex, item, list) => {
  const index = R.findIndex(matches(regex), list);
  return R.insert(index + 1, item, list);
});

export const generateExpirationValues = (expires?: Date) => {
  const now = dayjs();
  const expiration = expires ? dayjs(expires) : undefined;
  const expired = dayjs().isAfter(expiration ?? dayjs('1970-01-01T00:00:00'));
  const expiresSoon =
    !expired &&
    dayjs()
      .add(1, 'month')
      .isAfter(expiration ?? dayjs('1970-01-01T00:00:00'));
  const untilExpiration = expiration
    ? Math.abs(expiration.diff(now, 'd'))
    : undefined;

  return { expiration, expired, expiresSoon, untilExpiration };
};

export const getCustomerPropertyFn = createSelector(
  getUseCustomerRegistry,
  useCustomerRegistry => {
    function getCustomerPropertyName(name: string) {
      switch (name) {
        case 'birthNote':
          return useCustomerRegistry
            ? 'householdCode'
            : 'factoringContractNumber';
        case 'customerCardNumber':
          return useCustomerRegistry ? 'customerCardNumber' : 'code';
        case 'code':
          // This was not explicitly requested but if WBU wants to remap standard customerCardNumber
          // then something has to be done about code
          return useCustomerRegistry ? 'code' : 'customerCardNumber';
        case 'leadCode':
          return useCustomerRegistry ? name : 'skype';
        default:
          return name;
      }
    }
    return {
      getCustomerPropertyName,
      getCustomerProperty(name: string, customer) {
        const propName = getCustomerPropertyName(name);
        return customer[propName];
      },
    };
  },
);
